import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { get } from '../api'
import { useAccount } from './useAccount'
import { RushSkin } from '../types/rush'
import { BaseURLS } from '../api/axios'
import { Pagination } from '../types/pagination'
import { formatSkinName } from '../utils/formatSkinName'
import { useFormatSkinName } from './useFormatName'

export interface SkinsResponse {
  skins: RushSkin[]
  pagination: Pagination
}

export interface UseRushBSkinsParams {
  page?: number
  pageSize?: number
  state?: 'ACTIVE' | 'EXCHANGED'
  enabled?: boolean
}

export const useRushBSkins = ({
  page = 1,
  pageSize = 2,
  state = 'ACTIVE',
  enabled = true,
}: UseRushBSkinsParams = {}) => {
  const formatSkinName_ = useFormatSkinName()
  const { isLogin } = useAccount()
  const { lang } = useParams()
  const [allSkins, setAllSkins] = useState<RushSkin[]>([])
  const [currentState, setCurrentState] = useState(state)
  const [hasMoreSkins, setHasMoreSkins] = useState(true)
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false)
  const queryKey = ['rushB-skins', pageSize]

  const { data, refetch, isFetching } = useQuery<SkinsResponse>({
    queryKey,
    queryFn: async () => {
      const params = {
        page,
        pageSize: Math.min(pageSize, 100),
        state: currentState,
      }
      const response: SkinsResponse = await get('/v1/skins', params, {
        baseURL: BaseURLS.rush,
      })
      setAllSkins(
        (response.skins || []).map((skin: RushSkin) => ({
          ...skin,
          treasure: {
            ...skin.treasure,
            name: formatSkinName_(skin.treasure?.name || ''),
          },
        })),
      )
      setHasMoreSkins(!!response.pagination?.hasNext)
      setPagination(response.pagination)
      return response
    },
    enabled: isLogin && enabled,
    refetchOnWindowFocus: false,
  })

  // 获取下一页皮肤数据
  const fetchNextSkins = useCallback(async () => {
    if (!pagination?.hasNext) return null
    setIsLoadingNextPage(true)
    try {
      const nextPageResponse = await get(
        '/v1/skins',
        {
          page: pagination?.page + 1,
          pageSize,
          state: currentState,
        },
        {
          baseURL: BaseURLS.rush,
        },
      )
      setAllSkins((prev) => [
        ...prev,
        ...(nextPageResponse.skins || []).map((skin: RushSkin) => ({
          ...skin,
          treasure: {
            ...skin.treasure,
            name: formatSkinName_(skin.treasure?.name || ''),
          },
        })),
      ])
      setHasMoreSkins(!!nextPageResponse.pagination?.hasNext)
      setPagination(nextPageResponse.pagination)
      return nextPageResponse
    } catch (err) {
      setError('fetch next page skins failed')
      console.error(err)
      throw err
    } finally {
      setIsLoadingNextPage(false)
    }
  }, [pagination, pageSize, currentState, formatSkinName_])

  const totalPrice = useMemo(() => {
    return allSkins.reduce((acc, skin) => acc + skin.value, 0)
  }, [allSkins])

  return {
    skins: allSkins,
    loading: isFetching,
    error,
    hasMoreSkins,
    fetchNextSkins,
    totalSkins: pagination?.total || 0,
    refetchSkins: refetch,
    totalPrice,
    isLoadingNextPage,
  }
}
